import React, { FC, useEffect, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns, SECTIONS } from './constants';
import { OnChangeOptionsType, GridFilterType, CustomRenderType, TableRowType } from 'src/types/table';
import { OrderIcon, ReceivingIcon, ClaimIcon, InventoryIcon } from 'src/components/SkladonIcon/Icons';
import { useCheckPermission } from 'src/utils/checkPermission';
import { getTasksGridFetch, confirmTaskFetch } from 'src/resources/tasksGrid/reducer';
import TableSimple from 'src/components/TableSimple';
import Checkbox from 'src/components/Checkbox';
import Tooltip from 'src/components/Tooltip';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import StatusLabel from 'src/components/StatusLabel';
import getStatusIcon from './functions';

export const StatusNoIcon = styled('div')({
    width: '36px',
    height: '36px',
    marginRight: '10px',
});

const initTableFilters: GridFilterType[] = [];

const TasksGridContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [tableFilters, setTableFilters] = useState<GridFilterType[]>(initTableFilters);

    const loading = useSelector((state: RootStoreType) => state.tasksGrid.loading);
    const tasksData = useSelector((state: RootStoreType) => state.tasksGrid.data);

    const permissionOrderDetail = useCheckPermission('ORDER.DETAIL');
    const permissionReceivingDetail = useCheckPermission('RECEIVING.DETAIL');
    const permissionClaimDetail = useCheckPermission('CLAIM.DETAIL');
    const permissionTasksConfirm = useCheckPermission('TASKS.CONFIRM');

    const columns = permissionTasksConfirm
        ? allColumns
        : allColumns.filter((column) => column.key !== 'check');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('tasks.title') }));
    }, []);

    // const debouncedTableFilters = useDebounce(tableFilters, 500);

    // useEffect(() => {
    //     loadTableData();
    // }, [debouncedTableFilters]);

    useEffect(() => {
        loadTableData();
    }, []);

    const loadTableData = () => {
        if (tableFilters) {
            dispatch(getTasksGridFetch({ params: {} }));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableFilters(newOptions.filters || []);
    };

    const reloadTableData = () => {
        dispatch(getTasksGridFetch(undefined));
    };

    const confirmTask = (row: TableRowType) => {
        dispatch(confirmTaskFetch(row.ID));
    };

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'statusText':
                return (
                    <StatusLabel
                        icon={getStatusIcon(row) || <StatusNoIcon />}
                        label={row.statusText}
                        color={row.statusColor}
                    />
                );
            case 'check':
                if (!permissionTasksConfirm) {
                    return null;
                }
                if (row.type === 'notification') {
                    return (
                        <Tooltip title={translate('tasks.action.confirm')}>
                            <div>
                                <Checkbox value={false} onChange={(value: number | boolean) => confirmTask(row)} />
                            </div>
                        </Tooltip>
                    );
                }
                return null;
            default:
                return row[column.key];
        }
    };

    const handleClickProducts: (row: TableRowType) => void = (row) => {
        const rowData: { products: { ID: number; SKU: string }[] } = JSON.parse(row.JSON_DATA);
        const newProductsOptions = {
            filters: [
                {
                    column: 'ID',
                    operator: 'IN',
                    value: rowData.products
                        .map((product) => product.ID),
                },
            ],
            sort: { column: 'SKU', direction: 'DESC' },
            page: 1,
            items: 20,
            totalCount: 2,
        };
        sessionStorage.setItem('productsTable', JSON.stringify(newProductsOptions));
        navigate('/products/active/');
    };
    return (
        <>
            <TopBlock reloadTableData={reloadTableData} />
            <TableSimple
                allColumns={columns}
                rowIdKey="ID"
                rows={tasksData || []}
                loading={loading}
                columsLabelPrefix="tasks.table"
                onChangeOptions={handleChangeOptions}
                initSort={{ column: 'CREATED_AT', direction: 'DESC' }}
                rowActions={[
                    {
                        icon: <OrderIcon />,
                        label: translate('tasks.action.orderDetail'),
                        getLink: (row) => `/orders/${row.itemData.ID}/`,
                        hiddenFce: (row) => row.SECTION !== 'ORDER' || !permissionOrderDetail,
                    },
                    {
                        icon: <ReceivingIcon />,
                        label: translate('tasks.action.receivingDetail'),
                        getLink: (row) => `/receivings/${row.itemData.ID}/`,
                        hiddenFce: (row) => row.SECTION !== 'RECEIVING' || !permissionReceivingDetail,
                    },
                    {
                        icon: <ClaimIcon />,
                        label: translate('tasks.action.claimDetail'),
                        getLink: (row) => `/claims/${row.itemData.ID}/`,
                        hiddenFce: (row) => row.SECTION !== 'CLAIM' || !permissionClaimDetail,
                    },
                    {
                        icon: <InventoryIcon />,
                        label: translate('tasks.action.productsList'),
                        hiddenFce: (row) => row.SECTION !== 'PRODUCT' || !permissionClaimDetail,
                        onClick: handleClickProducts,
                    },
                ]}
                selectOptions={{
                    SECTIONS: SECTIONS,
                }}
                customRender={customRender}
                fullHeight
            />
        </>
    );
};

export default TasksGridContainer;
